import { useState, useRef, useEffect } from 'react';
import { ArrowRight, Download, Trash2, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { DdqSession } from '@/types/api.types';

/** Supported export formats for session data. */
export type ExportFormat = 'xlsx' | 'csv';

/** Props for the SessionActions component. */
export interface SessionActionsProps {
  /** The session whose actions are being rendered. */
  session: DdqSession;
  /** Called when the user clicks the Review button. */
  onReview: () => void;
  /** Called with the chosen format when the user selects an export option. */
  onExport: (format: ExportFormat) => void;
  /** Called when the user clicks the delete button. */
  onDelete: () => void;
  /** When true, disables all action buttons. Defaults to false. */
  disabled?: boolean;
}

/**
 * Renders the action toolbar for a single DDQ session row.
 *
 * Displays three controls:
 * - **Review** button — enabled only when `session.status === 'Ready'`.
 * - **Export** dropdown — offers XLSX and CSV options; enabled only when `session.status === 'Ready'`.
 * - **Delete** button — disabled while `session.status === 'Processing'`.
 */
export function SessionActions({
  session,
  onReview,
  onExport,
  onDelete,
  disabled = false,
}: SessionActionsProps) {
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const isReady = session.status === 'Ready';
  const isProcessing = session.status === 'Processing';

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setExportMenuOpen(false);
      }
    }

    if (exportMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [exportMenuOpen]);

  const handleExport = (format: ExportFormat) => {
    onExport(format);
    setExportMenuOpen(false);
  };

  return (
    <div className="flex items-center gap-2">
      <Button
        variant="default"
        size="sm"
        onClick={onReview}
        disabled={disabled || !isReady}
      >
        Review
        <ArrowRight className="h-4 w-4 ml-1" />
      </Button>

      {/* Export Dropdown */}
      <div className="relative" ref={menuRef}>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setExportMenuOpen(!exportMenuOpen)}
          disabled={disabled || !isReady}
          className="gap-1"
        >
          <Download className="h-4 w-4" />
          <ChevronDown className="h-3 w-3" />
        </Button>

        {exportMenuOpen && (
          <div className="absolute right-0 top-full mt-1 z-50 min-w-[120px] rounded-md border bg-popover p-1 shadow-md">
            <button
              onClick={() => handleExport('xlsx')}
              className={cn(
                'flex w-full items-center rounded-sm px-2 py-1.5 text-sm',
                'hover:bg-accent hover:text-accent-foreground',
                'cursor-pointer'
              )}
            >
              Export as XLSX
            </button>
            <button
              onClick={() => handleExport('csv')}
              className={cn(
                'flex w-full items-center rounded-sm px-2 py-1.5 text-sm',
                'hover:bg-accent hover:text-accent-foreground',
                'cursor-pointer'
              )}
            >
              Export as CSV
            </button>
          </div>
        )}
      </div>

      <Button
        variant="ghost"
        size="sm"
        onClick={onDelete}
        disabled={disabled || isProcessing}
        className="text-muted-foreground hover:text-destructive"
        title="Delete session"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}
